import { addDays, fromYmd, monthBounds, startOfWeek, yearMonth, ymd } from "./dates.js";

/**
 * What a dashboard is measuring, and what it is measuring it against.
 *
 * Pure, and shared, so the API that computes the figures and the SPA that labels
 * them agree on what "previous period" means. `today` is the business's day:
 * pass `businessDate()`, never `new Date()`.
 */
export type DashboardWindow = {
  /** The range asked for, inclusive. Defaults to the current calendar month. */
  from: string;
  to: string;
  /**
   * The last day actually measured: `to`, or today if the range runs past it.
   * "This month" on the 16th is the 1st to the 16th, and so is its comparison,
   * which is what keeps the delta from reading -50% on the 16th of every month.
   */
  through: string;
  prevFrom: string;
  prevTo: string;
  /** "vs last month". Says what the delta is against, so a number never floats. */
  compareLabel: string;
  /** The first Monday of the trend: twelve weeks ending with the week of `through`. */
  trendFrom: string;
};

/** How many weeks the trend charts show. Enough for a quarter, few enough to read. */
export const DASHBOARD_TREND_WEEKS = 12;

const DAY_MS = 24 * 60 * 60 * 1000;

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

/** `d` moved back `months` calendar months, its day clamped to the shorter month. */
function monthsBack(d: Date, months: number): Date {
  const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - months, 1));
  const last = monthBounds(yearMonth(first)).last;
  return new Date(
    Date.UTC(
      first.getUTCFullYear(),
      first.getUTCMonth(),
      Math.min(d.getUTCDate(), last.getUTCDate())
    )
  );
}

export function dashboardWindow(
  range: { from?: string; to?: string },
  today: Date
): DashboardWindow {
  const month = monthBounds(yearMonth(today));
  const from = range.from ?? ymd(month.first);
  const to = range.to ?? (range.from ? ymd(today) : ymd(month.last));

  const start = fromYmd(from);
  const end = fromYmd(to);
  // A range wholly in the future is measured in full; it simply holds nothing yet.
  const through =
    end.getTime() > today.getTime() && start.getTime() <= today.getTime() ? today : end;

  // A range of whole calendar months compares with the same months before it
  // ("vs last month", "vs last quarter"), day-aligned, so 1-16 October is set
  // against 1-16 September. Anything else compares with the same number of
  // days immediately before it.
  const wholeMonths =
    start.getUTCDate() === 1 && ymd(monthBounds(yearMonth(end)).last) === to;
  let prevFrom: Date;
  let prevTo: Date;
  let compareLabel: string;
  if (wholeMonths) {
    const span =
      (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
      (end.getUTCMonth() - start.getUTCMonth()) +
      1;
    prevFrom = monthsBack(start, span);
    prevTo = monthsBack(through, span);
    compareLabel =
      span === 1
        ? "vs last month"
        : span === 3
          ? "vs last quarter"
          : `vs previous ${span} months`;
  } else {
    const days = daysBetween(start, through) + 1;
    prevTo = addDays(start, -1);
    prevFrom = addDays(prevTo, -(days - 1));
    compareLabel = days === 7 ? "vs previous week" : `vs previous ${days} days`;
  }

  return {
    from,
    to,
    through: ymd(through),
    prevFrom: ymd(prevFrom),
    prevTo: ymd(prevTo),
    compareLabel,
    trendFrom: ymd(addDays(startOfWeek(through), -7 * (DASHBOARD_TREND_WEEKS - 1))),
  };
}

/**
 * The change from `previous` to `current` as a fraction (0.084 is +8.4%), or
 * null when there is nothing to compare with. A rise from zero is not "+∞%":
 * it is no comparison, and the card says nothing rather than something silly.
 */
export function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

/** A pending invite this close to expiry is worth resending before it lapses. */
export const INVITE_EXPIRING_DAYS = 3;

/** How far ahead the directory dashboard looks for assignments coming to an end. */
export const ENDING_SOON_DAYS = 30;
